class FeaturedProductDetails extends HTMLElement {
  connectedCallback() {
    if (this.resizeObserver) return;

    this.abortController = new AbortController();
    this.addEventListener('click', async (event) => {
      const link = event.target.closest('[data-detail-second-image]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const gallery = link.parentElement.querySelector('product-gallery');
      if (!gallery?.initPhotoSwipe) return;
      event.preventDefault();
      const images = [...gallery.querySelectorAll('[data-pswp-src]')];
      const index = images.findIndex((media) => media.dataset.mediaId === link.dataset.detailSecondImage);
      const lightbox = await gallery.initPhotoSwipe();
      if (lightbox && index >= 0) lightbox.loadAndOpen(index);
      else window.location.assign(link.href);
    }, { signal: this.abortController.signal });
    this.items = [...this.querySelectorAll('[data-featured-product-item]')];
    this.pairQuery = window.matchMedia('(min-width: 1100px)');
    this.syncThumbnails = this.syncThumbnails.bind(this);
    this.pairQuery.addEventListener('change', this.syncThumbnails);
    this.galleryObserver = new MutationObserver((records) => {
      if (records.some((record) => record.target.matches('[data-media-id]'))) this.syncThumbnails();
    });
    this.items.forEach((item) => {
      const gallery = item.querySelector('product-gallery');
      if (gallery) this.galleryObserver.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
    });
    this.syncThumbnails();
    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.updateGalleryHeights = this.updateGalleryHeights.bind(this);
    this.resizeObserver = new ResizeObserver(this.updateGalleryHeights);

    this.items.forEach((item) => {
      const info = item.querySelector('[data-featured-product-info]');
      if (info) this.resizeObserver.observe(info);
    });

    this.mediaQuery.addEventListener('change', this.updateGalleryHeights);
    requestAnimationFrame(this.updateGalleryHeights);
  }

  disconnectedCallback() {
    this.galleryObserver?.disconnect();
    this.pairQuery?.removeEventListener('change', this.syncThumbnails);
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mediaQuery?.removeEventListener('change', this.updateGalleryHeights);
  }

  syncThumbnails() {
    this.items.forEach((item) => {
      const gallery = item.querySelector('product-gallery');
      if (!gallery) return;
      const active = gallery.querySelector('[data-media-id]:not([hidden])');
      const secondary = this.pairQuery.matches ? item.querySelector('[data-detail-second-image]') : null;
      const buttons = [...gallery.querySelectorAll('[data-media-target]')];
      buttons.forEach((button) => {
        const media = [...gallery.querySelectorAll('[data-media-id]')].find((candidate) => candidate.id === button.dataset.mediaTarget);
        const hide = media === active || Boolean(secondary && media?.dataset.mediaId === secondary.dataset.detailSecondImage);
        if (hide && button.contains(document.activeElement)) gallery.querySelector('[data-product-zoom]:not([hidden])')?.focus({ preventScroll: true });
        button.hidden = hide;
      });
      const shell = gallery.querySelector('.tm-product-gallery__thumbs-shell');
      const visibleCount = buttons.filter((button) => !button.hidden).length;
      const empty = visibleCount === 0;
      if (shell) {
        shell.hidden = empty;
        shell.toggleAttribute('data-single-thumbnail', visibleCount === 1);
      }
      gallery.classList.toggle('tm-product-gallery--remaining-empty', empty);
      requestAnimationFrame(() => gallery.updateThumbnailControls?.());
    });
  }

  updateGalleryHeights() {
    const desktop = this.mediaQuery.matches;
    const minimum = Number(this.dataset.minImageHeight || 420);
    const maximum = Number(this.dataset.maxImageHeight || 720);
    const useSharedHeight = this.dataset.columns !== '1';
    const measuredHeights = this.items.map((item) => {
      const info = item.querySelector('[data-featured-product-info]');
      return info ? info.getBoundingClientRect().height : 0;
    });
    const sharedHeight = Math.min(maximum, Math.max(minimum, ...measuredHeights));

    this.items.forEach((item, index) => {
      const gallery = item.querySelector('[data-featured-product-gallery]');
      const info = item.querySelector('[data-featured-product-info]');
      if (!gallery || !info) return;

      if (!desktop) {
        gallery.style.removeProperty('--featured-media-height');
        return;
      }

      const measuredHeight = useSharedHeight ? sharedHeight : measuredHeights[index];
      const height = Math.min(maximum, Math.max(minimum, Math.ceil(measuredHeight)));
      gallery.style.setProperty('--featured-media-height', `${height}px`);
    });
  }
}

if (!customElements.get('featured-product-details')) {
  customElements.define('featured-product-details', FeaturedProductDetails);
}
